import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { track } from '../services/tracker';
import './PremiumModelNudge.css';

type Props = { source: 'book_generation' | 'chapter_generation' };

// Session-only: a dismissal survives progress updates, minimising, and remounts.
function wasDismissed(key: string): boolean {
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
}

function Nudge({ source, userId }: Props & { userId: number }) {
  const key = `sai:premium-model-nudge:v1:${userId}`;
  const [dismissed, setDismissed] = useState(() => wasDismissed(key));
  const viewed = useRef(false);

  useEffect(() => {
    if (!dismissed && !viewed.current) {
      viewed.current = true;
      track('premium_model_nudge_view', { source });
    }
  }, [dismissed, source]);

  if (dismissed) return null;
  // One line of text, not a card. It sits between the Generate button and the
  // chapter the user is waiting on, so a bordered panel with a heading, a
  // button-weight link and fine print read as a second status box competing
  // with the real one. Same words, one sentence, out of the way.
  return (
    <p className="premium-model-nudge">
      Premium AI model choice comes with Author and above — this draft is unaffected.{' '}
      <a href={`/subscription?source=${source}_premium_models`} target="_blank" rel="noopener noreferrer"
        onClick={() => track('premium_model_nudge_click', { source })}>
        Explore upgrades <span aria-hidden="true">↗</span>
        <span className="premium-model-nudge-sr"> (opens in a new tab)</span>
      </a>
      <button type="button" className="premium-model-nudge-dismiss"
        aria-label="Dismiss premium model suggestion"
        onClick={() => {
          setDismissed(true);
          try { sessionStorage.setItem(key, '1'); } catch { /* Optional storage. */ }
          track('premium_model_nudge_dismiss', { source });
        }}>×</button>
    </p>
  );
}

export default function PremiumModelNudge(props: Props) {
  const { user, loading } = useAuth();
  // Never treat an unknown plan, a paid plan, or lifetime access as free.
  if (loading || !user || Number(user.current_plan_id) !== 1 || user.lifetime_plan) return null;
  return <Nudge key={user.id} {...props} userId={user.id} />;
}
